<!-- src/components/stock/StockDatabase.vue -->

<template>
  <div class="stock-database">
    <div class="card management-card">
      <div class="management-tabs" role="tablist" aria-label="Stock management">
        <button
          id="stock-entry-tab"
          class="management-tab"
          :class="{ active: activeManagementTab === 'entry' }"
          type="button"
          role="tab"
          :aria-selected="activeManagementTab === 'entry'"
          aria-controls="stock-entry-panel"
          @click="activeManagementTab = 'entry'"
        >
          Stock Database Entry
        </button>

        <button
          id="stock-options-tab"
          class="management-tab"
          :class="{ active: activeManagementTab === 'options' }"
          type="button"
          role="tab"
          :aria-selected="activeManagementTab === 'options'"
          aria-controls="stock-options-panel"
          @click="activeManagementTab = 'options'"
        >
          Stock Options
        </button>

        <button
          id="stock-recipes-tab"
          class="management-tab"
          :class="{ active: activeManagementTab === 'recipes' }"
          type="button"
          role="tab"
          :aria-selected="activeManagementTab === 'recipes'"
          aria-controls="stock-recipes-panel"
          @click="activeManagementTab = 'recipes'"
        >
          Stock Recipes
        </button>

        <button
          id="stock-approvals-tab"
          class="management-tab"
          :class="{ active: activeManagementTab === 'approvals' }"
          type="button"
          role="tab"
          :aria-selected="activeManagementTab === 'approvals'"
          aria-controls="stock-approvals-panel"
          @click="activeManagementTab = 'approvals'"
        >
          <span>Order Approvals</span>

          <span
            v-if="pendingApprovalCount > 0"
            class="approval-tab-badge"
            :aria-label="`${pendingApprovalCount} new orders awaiting approval`"
          >
            {{ pendingApprovalCount }} new
          </span>
        </button>
      </div>

      <!-- =====================================================
           RECIPES
           ===================================================== -->

      <div
        v-show="activeManagementTab === 'recipes'"
        id="stock-recipes-panel"
        class="card-content management-tab-panel"
        role="tabpanel"
        aria-labelledby="stock-recipes-tab"
      >
        <StockRecipes :stock-items="items" />
      </div>

      <!-- =====================================================
           STOCK DATABASE ENTRY
           ===================================================== -->

      <div
        v-show="activeManagementTab === 'entry'"
        id="stock-entry-panel"
        class="card-content management-tab-panel"
        role="tabpanel"
        aria-labelledby="stock-entry-tab"
      >
        <div v-if="error" class="card error-card">
          <div class="card-content">
            <span class="red-text error-text">
              {{ error }}
            </span>
          </div>
        </div>

        <form @submit.prevent="saveItem">
          <div class="stock-form-grid">
            <div class="stock-field item-name-field">
              <label for="stock-item-name"> Item Name </label>

              <input
                id="stock-item-name"
                v-model.trim="form.item_name"
                type="text"
                maxlength="160"
                autocomplete="off"
                required
              />
            </div>

            <div class="stock-field">
              <label for="stock-unit-cost"> Order Unit Cost </label>

              <div class="money-input">
                <span class="currency-prefix"> £ </span>

                <input
                  id="stock-unit-cost"
                  v-model="form.unit_cost"
                  type="number"
                  min="0"
                  step="0.01"
                  inputmode="decimal"
                  required
                />
              </div>
            </div>

            <div class="stock-field">
              <label for="stock-order-unit"> Order Unit </label>

              <select
                id="stock-order-unit"
                v-model="form.order_unit"
                class="browser-default stock-select"
                required
              >
                <option value="" disabled>Select</option>

                <option v-for="unit in orderUnits" :key="unit" :value="unit">
                  {{ unit }}
                </option>
              </select>
            </div>

            <div class="stock-field">
              <label for="stock-stock-unit"> Stock Unit </label>

              <select
                id="stock-stock-unit"
                v-model="form.stock_unit"
                class="browser-default stock-select"
                required
              >
                <option value="" disabled>Select</option>

                <option v-for="unit in stockUnits" :key="unit" :value="unit">
                  {{ unit }}
                </option>
              </select>
            </div>

            <div class="stock-field">
              <label for="stock-units-per-order"> Stock Units per Order Unit </label>

              <input
                id="stock-units-per-order"
                v-model="form.stock_units_per_order_unit"
                type="number"
                min="0.1"
                step="0.1"
                inputmode="decimal"
                required
              />
            </div>

            <div class="stock-field">
              <label for="sales-units-per-stock"> Sales Units per Stock Unit </label>

              <input
                id="sales-units-per-stock"
                v-model="form.sales_units_per_stock_unit"
                type="number"
                min="0.1"
                step="0.1"
                inputmode="decimal"
                required
              />
            </div>

            <div class="stock-field">
              <label for="stock-supplier"> Supplier </label>

              <select
                id="stock-supplier"
                v-model="form.supplier"
                class="browser-default stock-select"
                required
              >
                <option value="" disabled>Select</option>

                <option v-for="supplier in suppliers" :key="supplier" :value="supplier">
                  {{ supplier }}
                </option>
              </select>
            </div>

            <div class="stock-field">
              <label for="stock-product-group"> Product Group </label>

              <select
                id="stock-product-group"
                v-model="form.product_group"
                class="browser-default stock-select"
                required
              >
                <option value="" disabled>Select</option>

                <option v-for="group in productGroups" :key="group" :value="group">
                  {{ group }}
                </option>
              </select>
            </div>
          </div>

          <div v-if="conversionPreview" class="conversion-summary">
            <i class="material-icons"> calculate </i>

            <span>
              1
              <strong>{{ form.order_unit }}</strong>
              =
              <strong>
                {{ formatNumber(form.stock_units_per_order_unit) }}
                {{ form.stock_unit }}
              </strong>

              <span class="conversion-divider">|</span>

              1
              <strong>{{ form.stock_unit }}</strong>
              =
              <strong>
                {{ formatNumber(form.sales_units_per_stock_unit) }}
                Sales Units
              </strong>
            </span>
          </div>

          <div class="form-footer">
            <div class="form-actions">
              <button
                class="btn teal darken-2 waves-effect waves-light"
                type="submit"
                :disabled="busy || !formValid"
              >
                <i class="material-icons left">
                  {{ editingId ? "save" : "add" }}
                </i>

                {{
                  busySave ? "Saving..." : editingId ? "Save Changes" : "Add Stock Item"
                }}
              </button>

              <button
                v-if="editingId"
                class="btn-flat waves-effect"
                type="button"
                :disabled="busy"
                @click="resetForm"
              >
                Cancel
              </button>
            </div>

            <p class="grey-text text-darken-1 duplicate-note">
              Duplicate item names are allowed.
            </p>
          </div>
        </form>
      </div>

      <!-- =====================================================
           STOCK OPTIONS
           ===================================================== -->

      <div
        v-show="activeManagementTab === 'options'"
        id="stock-options-panel"
        class="card-content options-body management-tab-panel"
        role="tabpanel"
        aria-labelledby="stock-options-tab"
      >
        <div v-if="optionError" class="card error-card option-error-card">
          <div class="card-content">
            <span class="red-text error-text">
              {{ optionError }}
            </span>
          </div>
        </div>

        <div class="option-grid">
          <section
            v-for="section in optionSections"
            :key="section.key"
            class="option-panel"
          >
            <div class="option-panel-heading">
              <h3>
                {{ section.label }}
              </h3>

              <span class="option-total">
                {{ section.values.length }}
                {{ section.values.length === 1 ? "option" : "options" }}
              </span>
            </div>

            <form class="option-add-form" @submit.prevent="addOption(section)">
              <input
                v-model.trim="newOptions[section.key]"
                type="text"
                :placeholder="`Add ${section.singular}`"
                :aria-label="`Add ${section.singular}`"
                maxlength="160"
                autocomplete="off"
                :disabled="busyOption"
              />

              <button
                class="btn-small teal darken-2 waves-effect waves-light"
                type="submit"
                :disabled="busyOption || !newOptions[section.key].trim()"
                :aria-label="`Add ${section.singular}`"
              >
                <i class="material-icons"> add </i>
              </button>
            </form>

            <div v-if="!section.values.length" class="option-empty grey-text">
              No options have been added.
            </div>

            <ul v-else class="option-list">
              <li v-for="value in section.values" :key="value" class="option-row">
                <template v-if="isEditingOption(section.key, value)">
                  <input
                    :id="`option-edit-${section.key}`"
                    v-model.trim="optionEdit.value"
                    class="option-edit-input"
                    type="text"
                    maxlength="160"
                    autocomplete="off"
                    :disabled="busyOption"
                    @keyup.enter="saveOptionEdit(section)"
                    @keyup.esc="cancelOptionEdit"
                  />

                  <div class="option-actions">
                    <button
                      class="btn-small teal darken-2 waves-effect waves-light"
                      type="button"
                      title="Save"
                      :disabled="busyOption || !optionEdit.value.trim()"
                      @click="saveOptionEdit(section)"
                    >
                      <i class="material-icons"> save </i>
                    </button>

                    <button
                      class="btn-small grey lighten-1 waves-effect"
                      type="button"
                      title="Cancel"
                      :disabled="busyOption"
                      @click="cancelOptionEdit"
                    >
                      <i class="material-icons"> close </i>
                    </button>
                  </div>
                </template>

                <template v-else>
                  <div class="option-name-wrap">
                    <span class="option-name">
                      {{ value }}
                    </span>

                    <span class="option-usage">
                      {{ optionUseCount(section, value) }}
                      {{ optionUseCount(section, value) === 1 ? "item" : "items" }}
                    </span>
                  </div>

                  <div class="option-actions">
                    <button
                      class="btn-small teal darken-2 waves-effect waves-light"
                      type="button"
                      title="Edit"
                      :disabled="busyOption"
                      @click="startOptionEdit(section.key, value)"
                    >
                      <i class="material-icons"> edit </i>
                    </button>

                    <button
                      class="btn-small red darken-1 waves-effect waves-light"
                      type="button"
                      title="Delete"
                      :disabled="busyOption"
                      @click="deleteOption(section, value)"
                    >
                      <i class="material-icons"> delete </i>
                    </button>
                  </div>
                </template>
              </li>
            </ul>
          </section>
        </div>

        <p class="option-help grey-text text-darken-1">
          Renaming an option updates every stock item that uses it. An option cannot be
          deleted until it is no longer used by a stock item.
        </p>
      </div>

      <!-- =====================================================
           ORDER APPROVALS
           ===================================================== -->

      <div
        v-show="activeManagementTab === 'approvals'"
        id="stock-approvals-panel"
        class="card-content management-tab-panel approvals-panel"
        role="tabpanel"
        aria-labelledby="stock-approvals-tab"
      >
        <StockOrderApprovals @pending-count="pendingApprovalCount = $event" />
      </div>
    </div>

    <!-- =====================================================
         STOCK DATABASE TABLE
         ===================================================== -->

    <div
      v-if="activeManagementTab === 'entry' || activeManagementTab === 'options'"
      class="card database-card"
    >
      <div
        class="database-header"
        role="button"
        tabindex="0"
        :aria-expanded="databaseOpen"
        @click="toggleDatabase"
        @keydown.enter="toggleDatabase"
        @keydown.space.prevent="toggleDatabase"
      >
        <div class="database-header-title">
          <i class="material-icons database-chevron">
            {{ databaseOpen ? "expand_more" : "chevron_right" }}
          </i>

          <div>
            <span class="database-title"> Stock Item Database </span>

            <div class="database-record-count">
              {{ filteredItems.length }}
              record{{ filteredItems.length === 1 ? "" : "s" }}
            </div>
          </div>
        </div>

        <div class="database-search" @click.stop @keydown.stop>
          <i class="material-icons search-icon"> search </i>

          <input
            id="stock-db-search"
            v-model.trim="search"
            type="text"
            placeholder="Search database"
            autocomplete="off"
            @focus="databaseOpen = true"
          />
        </div>
      </div>

      <div v-show="databaseOpen" class="database-body">
        <div v-if="busyLoad" class="progress">
          <div class="indeterminate"></div>
        </div>

        <div
          v-else-if="!filteredItems.length"
          class="empty-state grey-text text-darken-1"
        >
          {{
            search
              ? "No stock items match your search."
              : "No stock items have been added yet."
          }}
        </div>

        <div v-else class="table-wrap">
          <table class="striped highlight stock-table">
            <thead>
              <tr>
                <th>Item Name</th>

                <th class="right-align">Order Unit Cost</th>

                <th>Order Unit</th>

                <th>Stock Unit</th>

                <th class="right-align">Stock / Order</th>

                <th class="right-align">Sales / Stock</th>

                <th>Supplier</th>

                <th class="right-align">Actions</th>
              </tr>
            </thead>

            <tbody v-for="group in groupedItems" :key="group.name">
              <tr class="product-group-row">
                <td colspan="8">
                  <button
                    type="button"
                    class="product-group-toggle"
                    :aria-expanded="isProductGroupOpen(group.name)"
                    @click="toggleProductGroup(group.name)"
                  >
                    <i class="material-icons product-group-chevron" aria-hidden="true">
                      {{ isProductGroupOpen(group.name) ? "expand_more" : "chevron_right" }}
                    </i>
                    <strong>{{ group.name }}</strong>
                    <span class="product-count">
                      {{ group.items.length }}
                      item{{ group.items.length === 1 ? "" : "s" }}
                    </span>
                  </button>
                </td>
              </tr>

              <template v-if="isProductGroupOpen(group.name)">
                <tr v-for="item in group.items" :key="item.id">
                  <td class="item-name">
                    {{ item.item_name || "—" }}
                  </td>

                  <td class="right-align">
                    {{ money(item.unit_cost) }}
                  </td>

                  <td>
                    {{ item.order_unit || "—" }}
                  </td>

                  <td>
                    {{ item.stock_unit || "—" }}
                  </td>

                  <td class="right-align">
                    {{ displayConversion(item.stock_units_per_order_unit) }}
                  </td>

                  <td class="right-align">
                    {{ displayConversion(item.sales_units_per_stock_unit) }}
                  </td>

                  <td>
                    {{ item.supplier || "—" }}
                  </td>

                  <td class="right-align actions-cell">
                    <button
                      class="btn-small teal darken-2 waves-effect waves-light"
                      type="button"
                      :disabled="busy"
                      title="Edit"
                      @click="editItem(item)"
                    >
                      <i class="material-icons"> edit </i>
                    </button>

                    <button
                      class="btn-small red darken-1 waves-effect waves-light"
                      type="button"
                      :disabled="busy"
                      title="Delete"
                      @click="deleteItem(item)"
                    >
                      <i class="material-icons"> delete </i>
                    </button>
                  </td>
                </tr>
              </template>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  createStockUnit,
  deleteStockUnit,
  fetchStockUnits,
  replaceStockUnitOptionValue,
  updateStockUnit,
} from "@/scripts/sqlApiHelper/stockUnitsApi";

import {
  fetchStockOptions,
  saveStockOptions,
} from "@/scripts/sqlApiHelper/stockOptionsApi";

import StockOrderApprovals from "@/components/stock/StockOrderApprovals.vue";
import StockRecipes from "@/components/stock/StockRecipes.vue";

function blankForm() {
  return {
    item_name: "",
    unit_cost: "",
    order_unit: "",
    stock_unit: "",
    stock_units_per_order_unit: "",
    sales_units_per_stock_unit: "",
    supplier: "",
    product_group: "",
  };
}

function cleanOption(value) {
  return String(value ?? "")
    .trim()
    .replace(/\s+/g, " ");
}

function mergeUniqueOptions(...lists) {
  const result = [];
  const seen = new Set();

  for (const list of lists) {
    for (const rawValue of Array.isArray(list) ? list : []) {
      const value = cleanOption(rawValue);

      const key = value.toLocaleLowerCase("en-GB");

      if (value && !seen.has(key)) {
        seen.add(key);
        result.push(value);
      }
    }
  }

  return result;
}

export default {
  name: "StockDatabase",

  components: {
    StockOrderApprovals,
    StockRecipes,
  },

  data() {
    return {
      orderUnits: [],
      stockUnits: [],
      suppliers: [],
      productGroups: [],

      items: [],
      search: "",

      databaseOpen: true,
      productGroupOpen: {},
      activeManagementTab: "entry",

      pendingApprovalCount: 0,

      form: blankForm(),
      editingId: null,

      newOptions: {
        orderUnits: "",
        stockUnits: "",
        suppliers: "",
        productGroups: "",
      },

      optionEdit: {
        category: null,
        original: "",
        value: "",
      },

      busyLoad: false,
      busySave: false,
      busyDelete: false,
      busyOption: false,

      error: null,
      optionError: null,
    };
  },

  computed: {
    busy() {
      return this.busyLoad || this.busySave || this.busyDelete || this.busyOption;
    },

    optionSections() {
      return [
        {
          key: "orderUnits",
          label: "Order Units",
          singular: "order unit",
          itemField: "order_unit",
          values: this.orderUnits,
        },

        {
          key: "stockUnits",
          label: "Stock Units",
          singular: "stock unit",
          itemField: "stock_unit",
          values: this.stockUnits,
        },

        {
          key: "suppliers",
          label: "Suppliers",
          singular: "supplier",
          itemField: "supplier",
          values: this.suppliers,
        },

        {
          key: "productGroups",
          label: "Product Groups",
          singular: "product group",
          itemField: "product_group",
          values: this.productGroups,
        },
      ];
    },

    formValid() {
      const unitCost = Number(this.form.unit_cost);

      const stockUnitsPerOrder = Number(this.form.stock_units_per_order_unit);

      const salesUnitsPerStock = Number(this.form.sales_units_per_stock_unit);

      return (
        !!this.form.item_name.trim() &&
        Number.isFinite(unitCost) &&
        unitCost >= 0 &&
        this.orderUnits.includes(this.form.order_unit) &&
        this.stockUnits.includes(this.form.stock_unit) &&
        Number.isFinite(stockUnitsPerOrder) &&
        stockUnitsPerOrder > 0 &&
        Number.isFinite(salesUnitsPerStock) &&
        salesUnitsPerStock > 0 &&
        this.suppliers.includes(this.form.supplier) &&
        this.productGroups.includes(this.form.product_group)
      );
    },

    conversionPreview() {
      return Boolean(
        this.form.order_unit &&
          this.form.stock_unit &&
          Number(this.form.stock_units_per_order_unit) > 0 &&
          Number(this.form.sales_units_per_stock_unit) > 0
      );
    },

    filteredItems() {
      const needle = this.search.trim().toLowerCase();

      if (!needle) {
        return this.items;
      }

      return this.items.filter((item) =>
        [
          item.item_name,
          item.unit_cost,
          item.order_unit,
          item.stock_unit,
          item.stock_units_per_order_unit,
          item.sales_units_per_stock_unit,
          item.supplier,
          item.product_group,
        ]
          .map((value) => String(value ?? "").toLowerCase())
          .some((value) => value.includes(needle))
      );
    },

    groupedItems() {
      const groups = [];

      for (const groupName of this.productGroups) {
        const groupItems = this.filteredItems
          .filter((item) => item.product_group === groupName)
          .sort(this.compareItemNames);

        if (groupItems.length) {
          groups.push({
            name: groupName,
            items: groupItems,
          });
        }
      }

      const unassigned = this.filteredItems
        .filter((item) => !this.productGroups.includes(item.product_group))
        .sort(this.compareItemNames);

      if (unassigned.length) {
        groups.push({
          name: "Unassigned",
          items: unassigned,
        });
      }

      return groups;
    },
  },

  async mounted() {
    await this.loadDatabase();
  },

  methods: {
    compareItemNames(a, b) {
      return String(a.item_name ?? "").localeCompare(String(b.item_name ?? ""), "en", {
        sensitivity: "base",
      });
    },

    number(value) {
      const number = Number(value);

      return Number.isFinite(number) ? number : 0;
    },

    toggleDatabase() {
      this.databaseOpen = !this.databaseOpen;
    },

    isProductGroupOpen(groupName) {
      return this.productGroupOpen[groupName] === true;
    },

    toggleProductGroup(groupName) {
      this.productGroupOpen = {
        ...this.productGroupOpen,
        [groupName]: !this.isProductGroupOpen(groupName),
      };
    },

    async loadDatabase() {
      this.error = null;
      this.optionError = null;
      this.busyLoad = true;

      try {
        const [items, options] = await Promise.all([
          fetchStockUnits(),
          fetchStockOptions(),
        ]);

        this.items = Array.isArray(items) ? items : [];

        this.applyLoadedOptions(options);
      } catch (error) {
        console.error("[STOCK DATABASE] load failed:", error);

        this.items = [];

        this.error = error?.message || "Unable to load the stock database.";
      } finally {
        this.busyLoad = false;
      }
    },

    async loadItems() {
      this.error = null;
      this.busyLoad = true;

      try {
        this.items = await fetchStockUnits();

        this.addLegacyValuesToOptions();
      } catch (error) {
        console.error("[STOCK DATABASE] load failed:", error);

        this.items = [];

        this.error = error?.message || "Unable to load stock items.";
      } finally {
        this.busyLoad = false;
      }
    },

    applyLoadedOptions(options = {}) {
      this.orderUnits = mergeUniqueOptions(
        options.orderUnits,
        options.unitSizes,
        this.items.map((item) => item.order_unit)
      );

      this.stockUnits = mergeUniqueOptions(
        options.stockUnits,
        options.unitSizes,
        this.items.map((item) => item.stock_unit)
      );

      this.suppliers = mergeUniqueOptions(
        options.suppliers,
        this.items.map((item) => item.supplier)
      );

      this.productGroups = mergeUniqueOptions(
        options.productGroups,
        this.items.map((item) => item.product_group)
      );
    },

    addLegacyValuesToOptions() {
      this.orderUnits = mergeUniqueOptions(
        this.orderUnits,
        this.items.map((item) => item.order_unit)
      );

      this.stockUnits = mergeUniqueOptions(
        this.stockUnits,
        this.items.map((item) => item.stock_unit)
      );

      this.suppliers = mergeUniqueOptions(
        this.suppliers,
        this.items.map((item) => item.supplier)
      );

      this.productGroups = mergeUniqueOptions(
        this.productGroups,
        this.items.map((item) => item.product_group)
      );
    },

    optionsPayload(overrides = {}) {
      return {
        orderUnits: [...(overrides.orderUnits ?? this.orderUnits)],

        stockUnits: [...(overrides.stockUnits ?? this.stockUnits)],

        suppliers: [...(overrides.suppliers ?? this.suppliers)],

        productGroups: [...(overrides.productGroups ?? this.productGroups)],
      };
    },

    normalisedPayload() {
      return {
        item_name: this.form.item_name.trim(),

        unit_cost: Number(this.form.unit_cost),

        order_unit: this.form.order_unit,

        stock_unit: this.form.stock_unit,

        stock_units_per_order_unit: Number(this.form.stock_units_per_order_unit),

        sales_units_per_stock_unit: Number(this.form.sales_units_per_stock_unit),

        supplier: this.form.supplier,

        product_group: this.form.product_group,
      };
    },

    async saveItem() {
      if (!this.formValid || this.busySave) {
        return;
      }

      this.error = null;
      this.busySave = true;

      try {
        const payload = this.normalisedPayload();

        if (this.editingId) {
          await updateStockUnit(this.editingId, payload);
        } else {
          await createStockUnit(payload);
        }

        this.resetForm();

        await this.loadItems();
      } catch (error) {
        console.error("[STOCK DATABASE] save failed:", error);

        this.error = error?.message || "Unable to save stock item.";
      } finally {
        this.busySave = false;
      }
    },

    editItem(item) {
      this.error = null;

      this.editingId = item.id;

      this.activeManagementTab = "entry";

      this.form = {
        item_name: String(item.item_name ?? ""),

        unit_cost:
          item.unit_cost === null || item.unit_cost === undefined
            ? ""
            : Number(item.unit_cost),

        order_unit: String(item.order_unit || item.unit_size || ""),

        stock_unit: String(item.stock_unit || item.unit_size || ""),

        stock_units_per_order_unit:
          this.number(item.stock_units_per_order_unit) > 0
            ? this.number(item.stock_units_per_order_unit)
            : this.number(item.stock_units) > 0
            ? this.number(item.stock_units)
            : "",

        sales_units_per_stock_unit:
          this.number(item.sales_units_per_stock_unit) > 0
            ? this.number(item.sales_units_per_stock_unit)
            : this.number(item.sales_units) > 0
            ? this.number(item.sales_units)
            : "",

        supplier: String(item.supplier ?? ""),

        product_group: String(item.product_group ?? ""),
      };

      this.orderUnits = mergeUniqueOptions(this.orderUnits, [this.form.order_unit]);

      this.stockUnits = mergeUniqueOptions(this.stockUnits, [this.form.stock_unit]);

      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    },

    async deleteItem(item) {
      if (this.busyDelete) {
        return;
      }

      const description = [
        item.item_name || "Unnamed item",

        item.order_unit || "",

        item.stock_unit || "",

        item.supplier || "",
      ]
        .filter(Boolean)
        .join(" / ");

      const confirmed = window.confirm(
        `Delete this stock item?\n\n${description}\n\nThis cannot be undone.`
      );

      if (!confirmed) {
        return;
      }

      this.error = null;
      this.busyDelete = true;

      try {
        await deleteStockUnit(item.id);

        if (this.editingId === item.id) {
          this.resetForm();
        }

        await this.loadItems();
      } catch (error) {
        console.error("[STOCK DATABASE] delete failed:", error);

        this.error = error?.message || "Unable to delete stock item.";
      } finally {
        this.busyDelete = false;
      }
    },

    optionUseCount(section, value) {
      return this.items.filter((item) => item[section.itemField] === value).length;
    },

    hasOption(values, candidate, ignoredValue = null) {
      const candidateKey = cleanOption(candidate).toLocaleLowerCase("en-GB");

      const ignoredKey = cleanOption(ignoredValue).toLocaleLowerCase("en-GB");

      return values.some((value) => {
        const valueKey = cleanOption(value).toLocaleLowerCase("en-GB");

        return valueKey === candidateKey && valueKey !== ignoredKey;
      });
    },

    async addOption(section) {
      const value = cleanOption(this.newOptions[section.key]);

      if (!value || this.busyOption) {
        return;
      }

      if (this.hasOption(section.values, value)) {
        this.optionError = `${value} already exists in ${section.label}.`;

        return;
      }

      this.optionError = null;
      this.busyOption = true;

      const nextValues = [...section.values, value];

      try {
        await saveStockOptions(
          this.optionsPayload({
            [section.key]: nextValues,
          })
        );

        this[section.key] = nextValues;

        this.newOptions[section.key] = "";
      } catch (error) {
        console.error("[STOCK OPTIONS] add failed:", error);

        this.optionError = error?.message || `Unable to add ${section.singular}.`;
      } finally {
        this.busyOption = false;
      }
    },

    startOptionEdit(category, value) {
      this.optionError = null;

      this.optionEdit = {
        category,
        original: value,
        value,
      };

      this.$nextTick(() => {
        document.getElementById(`option-edit-${category}`)?.focus();
      });
    },

    isEditingOption(category, value) {
      return this.optionEdit.category === category && this.optionEdit.original === value;
    },

    cancelOptionEdit() {
      this.optionEdit = {
        category: null,
        original: "",
        value: "",
      };
    },

    async saveOptionEdit(section) {
      if (this.busyOption || this.optionEdit.category !== section.key) {
        return;
      }

      const oldValue = this.optionEdit.original;

      const newValue = cleanOption(this.optionEdit.value);

      if (!newValue) {
        this.optionError = `${section.label} cannot contain a blank value.`;

        return;
      }

      if (newValue === oldValue) {
        this.cancelOptionEdit();

        return;
      }

      if (this.hasOption(section.values, newValue, oldValue)) {
        this.optionError = `${newValue} already exists in ${section.label}.`;

        return;
      }

      const nextValues = section.values.map((value) =>
        value === oldValue ? newValue : value
      );

      this.optionError = null;
      this.busyOption = true;

      try {
        await replaceStockUnitOptionValue(section.itemField, oldValue, newValue);

        await saveStockOptions(
          this.optionsPayload({
            [section.key]: nextValues,
          })
        );

        this[section.key] = nextValues;

        this.items = this.items.map((item) =>
          item[section.itemField] === oldValue
            ? {
                ...item,

                [section.itemField]: newValue,

                ...(section.itemField === "stock_unit"
                  ? {
                      unit_size: newValue,
                    }
                  : {}),
              }
            : item
        );

        if (this.form[section.itemField] === oldValue) {
          this.form[section.itemField] = newValue;
        }

        this.cancelOptionEdit();
      } catch (error) {
        console.error("[STOCK OPTIONS] edit failed:", error);

        this.optionError = error?.message || `Unable to rename ${section.singular}.`;
      } finally {
        this.busyOption = false;
      }
    },

    async deleteOption(section, value) {
      if (this.busyOption) {
        return;
      }

      const useCount = this.optionUseCount(section, value);

      if (useCount) {
        this.optionError =
          `${value} is used by ${useCount} stock ` +
          `item${useCount === 1 ? "" : "s"}. ` +
          "Edit those items before deleting it.";

        this.activeManagementTab = "options";

        return;
      }

      const confirmed = window.confirm(
        `Delete ${section.singular} "${value}"?\n\nThis cannot be undone.`
      );

      if (!confirmed) {
        return;
      }

      const nextValues = section.values.filter((option) => option !== value);

      this.optionError = null;
      this.busyOption = true;

      try {
        await saveStockOptions(
          this.optionsPayload({
            [section.key]: nextValues,
          })
        );

        this[section.key] = nextValues;

        if (this.form[section.itemField] === value) {
          this.form[section.itemField] = "";
        }

        if (this.isEditingOption(section.key, value)) {
          this.cancelOptionEdit();
        }
      } catch (error) {
        console.error("[STOCK OPTIONS] delete failed:", error);

        this.optionError = error?.message || `Unable to delete ${section.singular}.`;
      } finally {
        this.busyOption = false;
      }
    },

    resetForm() {
      this.editingId = null;
      this.form = blankForm();

      this.$nextTick(() => {
        document.getElementById("stock-item-name")?.focus();
      });
    },

    displayConversion(value) {
      const number = this.number(value);

      return number > 0 ? this.formatNumber(number) : "—";
    },

    formatNumber(value) {
      const number = Number(value);

      if (!Number.isFinite(number)) {
        return "0";
      }

      return number.toLocaleString("en-GB", {
        maximumFractionDigits: 1,
      });
    },

    money(value) {
      const amount = Number(value);

      if (!Number.isFinite(amount)) {
        return "£0.00";
      }

      return new Intl.NumberFormat("en-GB", {
        style: "currency",
        currency: "GBP",
      }).format(amount);
    },
  },
};
</script>

<style scoped>
.stock-database {
  box-sizing: border-box;
  width: 100%;
  padding: 0 32px 24px;
}

/*
 * Border radius comes from coredeck-ui.css.
 * overflow:hidden is specific to these cards.
 */
.management-card,
.database-card {
  overflow: hidden;
}

.error-card {
  margin-top: 18px;
  border: 1px solid #ef9a9a;
  border-radius: 8px;
}

.error-text {
  font-weight: 600;
}

/* =========================================================
   Management tabs
   ========================================================= */

.management-tabs {
  display: flex;
  min-height: 66px;
  padding: 0 22px;
  align-items: flex-end;
  gap: 0;
  border-bottom: 1px solid #d7d7d7;
  background: #ffffff;
}

/*
 * These intentionally override the generic global button
 * sizing because these buttons are tabs, not normal actions.
 */
.management-tab {
  position: relative;
  display: inline-flex !important;
  align-items: center !important;
  justify-content: center !important;
  gap: 8px !important;
  min-height: 66px !important;
  height: 66px !important;
  padding: 0 24px !important;
  border: 0 !important;
  border-radius: 0 !important;
  background: transparent !important;
  color: #607d8b;
  box-shadow: none !important;
  font-family: inherit;
  font-size: 1.05rem !important;
  font-weight: 600 !important;
  line-height: 1.2 !important;
  cursor: pointer;
  transition: color 0.15s ease, background-color 0.15s ease;
}

.approval-tab-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: auto;
  min-width: 0;
  height: 22px;
  padding: 0 7px;
  box-sizing: border-box;
  border-radius: 3px;
  background: #2e7d32;
  color: #ffffff;
  font-size: 0.72rem;
  font-weight: 700;
  line-height: 1;
  white-space: nowrap;
}

.management-tab:hover {
  background: #fafafa !important;
  color: #263238;
}

.management-tab + .management-tab {
  border-left: 1px solid #eeeeee !important;
}

.management-tab:focus-visible {
  outline: 2px solid #00897b;
  outline-offset: -3px;
}

.management-tab.active {
  color: #00796b;
}

.management-tab.active::after {
  position: absolute;
  right: 0;
  bottom: -1px;
  left: 0;
  height: 3px;
  background: #00897b;
  content: "";
}

.management-tab-panel {
  padding: 28px 32px 30px;
}

/* =========================================================
   Stock database form
   ========================================================= */

.stock-form-grid {
  display: grid;
  grid-template-columns:
    minmax(220px, 1.6fr)
    minmax(140px, 0.8fr)
    minmax(150px, 1fr)
    minmax(150px, 1fr);
  gap: 18px;
  align-items: end;
}

.stock-field {
  min-width: 0;
}

.stock-field label {
  display: block;
  position: static;
  margin: 0 0 7px;
  color: #607d8b;
  font-size: 0.82rem;
  font-weight: 600;
  transform: none;
}

.stock-field input {
  box-sizing: border-box !important;
  width: 100% !important;
  height: 42px !important;
  margin: 0 !important;
  padding: 0 10px !important;
  border: 1px solid #bdbdbd !important;
  border-radius: 4px !important;
  background: #ffffff !important;
  box-shadow: none !important;
  font-size: 15px;
}

.stock-field input:focus {
  border: 1px solid #00897b !important;
  box-shadow: 0 0 0 1px #00897b !important;
}

.stock-select {
  box-sizing: border-box;
  width: 100%;
  height: 42px;
  margin: 0;
  padding: 0 10px;
  border: 1px solid #bdbdbd;
  border-radius: 4px;
  background-color: #ffffff;
  color: #424242;
  font-size: 15px;
}

.stock-select:focus {
  border-color: #00897b;
  outline: none;
  box-shadow: 0 0 0 1px #00897b;
}

.money-input {
  position: relative;
}

.currency-prefix {
  position: absolute;
  top: 50%;
  left: 10px;
  z-index: 1;
  color: #616161;
  transform: translateY(-50%);
}

.money-input input {
  padding-left: 25px !important;
}

.conversion-summary {
  display: flex;
  align-items: center;
  gap: 9px;
  margin-top: 18px;
  padding: 11px 14px;
  border-radius: 6px;
  background: #e0f2f1;
  color: #00695c;
  font-size: 0.88rem;
}

.conversion-summary i {
  flex: 0 0 auto;
  font-size: 20px;
}

.conversion-divider {
  margin: 0 8px;
  opacity: 0.55;
}

.form-footer {
  display: flex;
  margin-top: 24px;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
}

.form-actions {
  display: flex;
  align-items: center;
  gap: 10px;
}

.duplicate-note {
  margin: 0;
  font-size: 0.9rem;
}

/* =========================================================
   Options
   ========================================================= */

.options-body {
  border-top: 0;
}

.option-error-card {
  margin: 0 0 20px;
}

.option-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 20px;
  align-items: start;
}

.option-panel {
  min-width: 0;
  padding: 18px;
  border: 1px solid #e0e0e0;
  border-radius: 10px;
  background: #fafafa;
}

.option-panel-heading {
  display: flex;
  margin-bottom: 14px;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
}

.option-panel-heading h3 {
  margin: 0;
  color: #263238;
  font-size: 1.15rem;
  font-weight: 600;
}

.option-total,
.option-usage {
  color: #757575;
  font-size: 0.8rem;
  white-space: nowrap;
}

.option-add-form {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 8px;
  margin-bottom: 14px;
}

.option-add-form input,
.option-edit-input {
  box-sizing: border-box !important;
  width: 100% !important;
  height: 36px !important;
  margin: 0 !important;
  padding: 0 9px !important;
  border: 1px solid #bdbdbd !important;
  border-radius: 4px !important;
  background: #ffffff !important;
  box-shadow: none !important;
}

.option-add-form input:focus,
.option-edit-input:focus {
  border-color: #00897b !important;
  box-shadow: 0 0 0 1px #00897b !important;
}

.option-list {
  max-height: 310px;
  margin: 0;
  overflow-y: auto;
  border-top: 1px solid #e0e0e0;
}

.option-row {
  display: flex;
  min-height: 50px;
  padding: 7px 0;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  border-bottom: 1px solid #e0e0e0;
}

.option-name-wrap {
  min-width: 0;
}

.option-name,
.option-usage {
  display: block;
}

.option-name {
  color: #37474f;
  font-weight: 600;
  overflow-wrap: anywhere;
}

.option-usage {
  margin-top: 2px;
}

.option-actions {
  display: flex;
  flex: 0 0 auto;
  gap: 5px;
}

/*
 * Normal .btn-small sizing is global.
 * These are deliberately narrower icon controls.
 */
.option-actions .btn-small,
.option-add-form .btn-small {
  padding: 0 8px !important;
}

.option-empty {
  padding: 18px 0;
  text-align: center;
}

.option-help {
  margin: 18px 0 0;
  font-size: 0.9rem;
}

/* =========================================================
   Database disclosure
   ========================================================= */

.database-header {
  box-sizing: border-box;
  display: flex;
  min-height: 92px;
  padding: 18px 22px;
  align-items: center;
  justify-content: space-between;
  gap: 30px;
  cursor: pointer;
  background: #ffffff;
  transition: background-color 0.15s ease;
}

.database-header:hover {
  background-color: #fafafa;
}

.database-header:focus {
  outline: none;
}

.database-header-title {
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  gap: 10px;
}

.database-chevron {
  flex: 0 0 auto;
  color: #455a64;
  font-size: 30px;
}

.database-title {
  display: block;
  color: #212121;
  font-size: 1.65rem;
  line-height: 1.2;
}

.database-record-count {
  margin-top: 4px;
  color: #757575;
  font-size: 0.95rem;
}

.database-search {
  display: flex;
  width: min(420px, 40vw);
  align-items: center;
  gap: 12px;
  cursor: default;
}

.search-icon {
  flex: 0 0 auto;
  color: #212121;
  font-size: 30px;
}

.database-search input {
  box-sizing: border-box !important;
  width: 100% !important;
  height: 44px !important;
  margin: 0 !important;
  padding: 0 4px !important;
  border: none !important;
  border-bottom: 1px solid #9e9e9e !important;
  border-radius: 0 !important;
  background: transparent !important;
  box-shadow: none !important;
  font-size: 16px;
}

.database-search input:focus {
  border-bottom: 2px solid #00897b !important;
  box-shadow: none !important;
}

.database-body {
  padding: 0 22px 22px;
  border-top: 1px solid #eeeeee;
}

/*
 * Horizontal scrolling for .table-wrap is supplied by
 * coredeck-ui.css.
 */
.stock-table {
  width: 100%;
}

.stock-table th {
  font-weight: 600;
}

.item-name {
  font-weight: 600;
}

.stock-table .product-group-row {
  background-color: #438c8f !important;
}

.stock-table .product-group-row td {
  padding: 0 !important;
  background-color: #438c8f !important;
}

.stock-table .product-group-row:hover td {
  background-color: #397d80 !important;
}

.stock-table .product-group-toggle {
  box-sizing: border-box;
  display: flex !important;
  width: 100% !important;
  min-height: 46px !important;
  height: auto !important;
  padding: 10px 14px !important;
  align-items: center !important;
  justify-content: flex-start !important;
  gap: 8px !important;
  border: 0 !important;
  border-radius: 0 !important;
  background: transparent !important;
  box-shadow: none !important;
  color: #ffffff !important;
  font-family: inherit !important;
  font-size: 1rem !important;
  font-weight: 500 !important;
  line-height: 1.3 !important;
  letter-spacing: normal !important;
  text-align: left !important;
  text-transform: none !important;
  white-space: normal !important;
  cursor: pointer;
}

.stock-table .product-group-toggle:focus-visible {
  outline: 3px solid #ffffff;
  outline-offset: -4px;
}

.stock-table .product-group-toggle strong {
  font-weight: 700;
  color: inherit;
}

.product-group-chevron {
  flex: 0 0 auto;
  font-size: 22px;
}

.product-count {
  margin-left: 2px;
  font-size: 0.85rem;
  font-weight: 400;
  opacity: 0.8;
}

.actions-cell {
  white-space: nowrap;
}

.actions-cell .btn-small {
  margin-left: 6px;
  padding: 0 10px !important;
}

.empty-state {
  padding: 28px 0;
  text-align: center;
}

/* =========================================================
   Responsive component-specific layout
   ========================================================= */

@media only screen and (max-width: 1250px) {
  .option-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media only screen and (max-width: 1100px) {
  .stock-database {
    padding: 0 20px 20px;
  }

  .stock-form-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media only screen and (max-width: 700px) {
  .option-grid {
    grid-template-columns: 1fr;
  }
}

@media only screen and (max-width: 600px) {
  .stock-database {
    padding: 0 12px 16px;
  }

  .management-tabs {
    min-height: 58px;
    padding: 0 8px;
    overflow-x: auto;
    align-items: stretch;
  }

  .management-tab {
    flex: 0 0 auto;
    min-height: 58px !important;
    height: 58px !important;
    padding: 0 10px !important;
    font-size: 0.82rem !important;
  }

  .approval-tab-badge {
    height: 20px;
    padding: 0 6px;
    font-size: 0.66rem;
  }

  .management-tab-panel {
    padding: 16px;
  }

  .stock-form-grid {
    grid-template-columns: 1fr;
    gap: 15px;
  }

  .form-footer,
  .form-actions {
    display: block;
  }

  /*
   * Standard .btn mobile width comes from coredeck-ui.css.
   * btn-flat is intentionally full width here.
   */
  .form-actions .btn-flat {
    width: 100% !important;
    margin: 4px 0 !important;
  }

  .duplicate-note {
    margin-top: 14px;
  }

  .option-panel {
    padding: 14px;
  }

  .conversion-summary {
    align-items: flex-start;
  }

  .conversion-divider {
    display: none;
  }

  .database-header {
    display: block;
    min-height: 0;
    padding: 16px;
  }

  .database-header-title {
    margin-bottom: 16px;
  }

  .database-title {
    font-size: 1.35rem;
  }

  .database-search {
    width: 100%;
  }

  .database-body {
    padding: 16px;
  }
}
</style>
